import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { Check, ImageIcon, Move, ZoomIn } from 'lucide-react'
import { type PointerEvent, type ReactNode, useEffect, useRef, useState } from 'react'

export type EditableImageValue = {
  url: string
  alt: string
  fit: 'cover' | 'contain'
  focalX: number
  focalY: number
  zoom: number
  radius: number
  height?: number
}

type ImageEditorDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description: string
  value: EditableImageValue
  defaultAlt: string
  uploadId: string
  previewBackground?: string
  showHeightControl?: boolean
  heightRange?: { min: number; max: number; step: number }
  extraControls?: ReactNode
  onChange: (value: EditableImageValue) => void
  onRemove: () => void
}

type CropRatioValue = 'free' | '1:1' | '4:3' | '3:2' | '16:9' | '3:4' | '2:3'

const cropRatioOptions: Array<{
  value: CropRatioValue
  label: string
  ratio?: number
}> = [
  { value: 'free', label: '自由' },
  { value: '1:1', label: '1:1', ratio: 1 },
  { value: '4:3', label: '4:3', ratio: 4 / 3 },
  { value: '3:2', label: '3:2', ratio: 3 / 2 },
  { value: '16:9', label: '16:9', ratio: 16 / 9 },
  { value: '3:4', label: '3:4', ratio: 3 / 4 },
  { value: '2:3', label: '2:3', ratio: 2 / 3 },
]

const cropGridCells = [
  'top-left',
  'top',
  'top-right',
  'left',
  'center',
  'right',
  'bottom-left',
  'bottom',
  'bottom-right',
]

const ratioPreviewWidth = 640

export function readImageFileAsDataUrl(file: File) {
  return new Promise<string>((resolve) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result ?? ''))
    reader.readAsDataURL(file)
  })
}

export function ImageEditorDialog({
  open,
  onOpenChange,
  title,
  description,
  value,
  defaultAlt,
  previewBackground = '#F1F5F9',
  heightRange = { min: 120, max: 520, step: 10 },
  onChange,
}: ImageEditorDialogProps) {
  const [draft, setDraft] = useState(() => normalizeImageValue(value, defaultAlt))
  const [selectedRatio, setSelectedRatio] = useState<CropRatioValue>('free')

  useEffect(() => {
    if (!open) return
    setDraft(normalizeImageValue(value, defaultAlt))
    setSelectedRatio('free')
  }, [defaultAlt, open, value])

  function updateDraft(patch: Partial<EditableImageValue>) {
    setDraft((current) => ({ ...current, ...patch }))
  }

  function selectRatio(nextRatio: (typeof cropRatioOptions)[number]) {
    setSelectedRatio(nextRatio.value)
    if (!nextRatio.ratio) {
      updateDraft({ fit: 'cover' })
      return
    }
    updateDraft({
      fit: 'cover',
      height: getHeightForRatio(nextRatio.ratio, heightRange),
    })
  }

  function confirmCrop() {
    onChange(draft)
    onOpenChange(false)
  }

  function cancelCrop() {
    setDraft(normalizeImageValue(value, defaultAlt))
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[min(calc(100vw-1.5rem),680px)] max-w-none overflow-hidden p-0">
        <div data-testid="image-editor-crop-dialog" className="flex max-h-[92dvh] min-h-0 flex-col">
          <div className="shrink-0 px-6 pt-5 pb-3 pr-14">
            <DialogHeader>
              <DialogTitle>{title}</DialogTitle>
              <DialogDescription className="sr-only">{description}</DialogDescription>
            </DialogHeader>
          </div>

          <div className="min-h-0 overflow-y-auto px-5 pb-5">
            <div
              className="rounded-lg p-3"
              style={{
                background: previewBackground,
              }}
            >
              <ImagePreviewFrame value={draft} onChange={updateDraft} />
            </div>

            <div className="mt-4 space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-slate-600">比例：</span>
                {cropRatioOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={cn(
                      'h-8 rounded-full px-3 text-sm font-medium transition-colors hover:bg-blue-50 hover:text-blue-700',
                      selectedRatio === option.value
                        ? 'bg-blue-600 text-white shadow-sm hover:bg-blue-600 hover:text-white'
                        : 'text-slate-600',
                    )}
                    onClick={() => selectRatio(option)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>

              <ImageSlider
                icon={<ZoomIn className="size-4 text-slate-500" />}
                label="縮放"
                value={draft.zoom}
                min={1}
                max={3}
                step={0.05}
                suffix="%"
                formatValue={(nextValue) => `${Math.round(nextValue * 100)}%`}
                onChange={(zoom) => updateDraft({ zoom })}
              />
            </div>
          </div>

          <div className="flex shrink-0 justify-end gap-3 border-t border-slate-200 bg-slate-50 px-6 py-4">
            <Button type="button" variant="outline" onClick={cancelCrop}>
              取消
            </Button>
            <Button type="button" onClick={confirmCrop}>
              <Check className="size-4" />
              確認裁剪
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function ImagePreviewFrame({
  value,
  onChange,
}: {
  value: EditableImageValue
  onChange: (patch: Partial<EditableImageValue>) => void
}) {
  const height = clamp(value.height ?? 360, 120, 520)
  const dragRef = useRef<{
    pointerId: number
    startX: number
    startY: number
    startFocalX: number
    startFocalY: number
    width: number
    height: number
  } | null>(null)

  function handlePointerDown(event: PointerEvent<HTMLDivElement>) {
    if (!value.url) return
    const bounds = event.currentTarget.getBoundingClientRect()
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startFocalX: value.focalX,
      startFocalY: value.focalY,
      width: bounds.width,
      height: bounds.height,
    }
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  function handlePointerMove(event: PointerEvent<HTMLDivElement>) {
    const drag = dragRef.current
    if (!drag || drag.pointerId !== event.pointerId) return
    const zoomDampening = 100 / Math.max(value.zoom, 1)
    const deltaX = ((event.clientX - drag.startX) / drag.width) * zoomDampening
    const deltaY = ((event.clientY - drag.startY) / drag.height) * zoomDampening

    onChange({
      focalX: roundTo(clamp(drag.startFocalX - deltaX, 0, 100), 1),
      focalY: roundTo(clamp(drag.startFocalY - deltaY, 0, 100), 1),
      fit: value.fit === 'contain' ? 'cover' : value.fit,
    })
  }

  function handlePointerEnd(event: PointerEvent<HTMLDivElement>) {
    if (dragRef.current?.pointerId === event.pointerId) {
      dragRef.current = null
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId)
      }
    }
  }

  return (
    <div
      className={cn(
        'group relative w-full overflow-hidden rounded-md bg-slate-100 shadow-sm',
        value.url && 'cursor-grab active:cursor-grabbing',
      )}
      style={{ height, touchAction: 'none' }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerEnd}
      onPointerCancel={handlePointerEnd}
    >
      {value.url ? (
        <>
          <img
            src={value.url}
            alt={value.alt || '圖片預覽'}
            className="size-full select-none"
            draggable={false}
            style={{
              objectFit: value.fit,
              objectPosition: `${value.focalX}% ${value.focalY}%`,
              transform: `scale(${value.zoom})`,
              transformOrigin: `${value.focalX}% ${value.focalY}%`,
            }}
          />
          <div className="pointer-events-none absolute inset-0 bg-slate-950/18" />
          <div className="pointer-events-none absolute inset-[5%] border border-dashed border-white/90 shadow-[0_0_0_999px_rgba(15,23,42,0.28)]">
            <CropCorner className="-top-1.5 -left-1.5" />
            <CropCorner className="-top-1.5 -right-1.5" />
            <CropCorner className="-bottom-1.5 -left-1.5" />
            <CropCorner className="-right-1.5 -bottom-1.5" />
            <div className="grid size-full grid-cols-3 grid-rows-3 opacity-80">
              {cropGridCells.map((cell) => (
                <div key={cell} className="border border-white/20" />
              ))}
            </div>
          </div>
          <div className="pointer-events-none absolute bottom-3 left-3 flex items-center gap-2 rounded-full bg-slate-950/72 px-3 py-1.5 text-xs font-medium text-white shadow-sm backdrop-blur">
            <Move className="size-3.5" />
            拖曳移動裁切
          </div>
        </>
      ) : (
        <div className="grid size-full place-items-center text-center text-sm text-slate-500">
          <span>
            <ImageIcon className="mx-auto mb-2 size-8 text-slate-400" />
            尚未設定圖片
          </span>
        </div>
      )}
    </div>
  )
}

function CropCorner({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        'absolute size-3 border border-white bg-slate-500/70 shadow-sm',
        className,
      )}
      aria-hidden="true"
    />
  )
}

function ImageSlider({
  icon,
  label,
  value,
  min,
  max,
  step,
  suffix,
  formatValue = (nextValue) => `${nextValue}${suffix}`,
  onChange,
}: {
  icon?: ReactNode
  label: string
  value: number
  min: number
  max: number
  step: number
  suffix: string
  formatValue?: (value: number) => string
  onChange: (value: number) => void
}) {
  const progress = ((value - min) / (max - min)) * 100
  return (
    <div className="grid grid-cols-[auto_1fr_auto] items-center gap-3">
      <span className="flex items-center gap-1.5 text-sm font-medium text-slate-600">
        {icon}
        {label}：
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        aria-label={label}
        onChange={(event) => onChange(Number(event.target.value))}
        className="h-2 w-full cursor-pointer appearance-none rounded-full accent-blue-600"
        style={{
          background: `linear-gradient(90deg, #3b82f6 0%, #db2777 ${progress}%, #e2e8f0 ${progress}%, #e2e8f0 100%)`,
        }}
      />
      <span className="min-w-12 text-right text-xs tabular-nums text-slate-500">
        {formatValue(value)}
      </span>
    </div>
  )
}

function normalizeImageValue(value: EditableImageValue, defaultAlt: string): EditableImageValue {
  return {
    ...value,
    alt: value.alt || defaultAlt,
    fit: value.fit ?? 'cover',
    focalX: value.focalX ?? 50,
    focalY: value.focalY ?? 50,
    zoom: value.zoom ?? 1,
    radius: value.radius ?? 12,
  }
}

function getHeightForRatio(ratio: number, range: { min: number; max: number; step: number }) {
  const rawHeight = ratioPreviewWidth / ratio
  const steppedHeight = Math.round(rawHeight / range.step) * range.step
  return clamp(steppedHeight, range.min, range.max)
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max)
}

function roundTo(value: number, digits: number) {
  const factor = 10 ** digits
  return Math.round(value * factor) / factor
}
